// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { IconWrapper } from "@insite/mobius/Icon";
import { Icon } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import CaretRight from "@werner/XDL/Icons/CaretRight";
import CaretUp from "@werner/XDL/Icons/CaretUp";
import { color, styledScrollbar } from "@werner/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    wrapper?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    wrapper: {
        css: css`
            margin-bottom: 60px;
        `,
    },
    accordionSection: {
        panelProps: {
            css: css`
                margin: 0;
                padding: 0;

                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
                ${styledScrollbar()}
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                padding-bottom: 5px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: center;
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: centert;
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    seeAllLink: {
        typographyProps: {
            css: css`
                &&&{
                    color: ${color("common.ui3")};
                    &:hover {
                        color: ${color("primary")};
                    }
                }
                ${IconWrapper} {
                    margin-left: 5px;
                }
            `,
        },
        css: css`
            margin-top: 15px;
        `,
    },
    seeLessLink: {
        typographyProps: {
            css: css`
                &&&{
                    color: ${color("common.ui3")};
                    &:hover {
                        color: ${color("primary")};
                    }
                }
                ${IconWrapper} {
                    margin-left: 5px;
                }
            `,
        },
        css: css`
            margin-top: 15px;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    // showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    const anySelected = facets.find(f => f.selected) !== undefined;
    const [accordionExpanded, setAccordionExpanded] = useState(expandByDefault || anySelected);
    const { showMoreLimit } = useAppSelector(state => ({
        showMoreLimit: getSettingsCollection(state).customSettings.numberOfAttributeValuesToDisplay,
    }));

    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);

    const toggleViewMore = () => {
        setAccordionExpanded(!accordionExpanded)
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <ManagedAccordionSection
                title={title}
                {...styles.accordionSection}
                initialExpanded={expandByDefault || anySelected}
                onTogglePanel={toggleViewMore}
            >
                {limitedFacets?.map(facet => (
                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`facetFilter${facet.id}`}
                        >
                            {facet.name} {facet.count !== -1 && `(${facet.count})`}
                        </Checkbox>
                    </CheckboxGroup>
                ))}
            </ManagedAccordionSection>
            {accordionExpanded && facets &&
                facets.length > showMoreLimit &&
                (expanded ? (
                    <Link {...styles.seeLessLink} onClick={() => setExpanded(false)} aria-label="View Less">
                        {translate("View Less")}
                        <Icon src={CaretUp} size={9} />
                    </Link>
                ) : (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(true)} aria-label="View More">
                        {translate("View More")}
                        <Icon src={CaretRight} size={9} />
                    </Link>
                ))}
        </StyledWrapper>
    );
};

export default ProductListFiltersAccordionSection;
